import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPartMarketplaces } from "../models/Part";
import { DefaultResponseWithData } from "../models/system";

/**
 * Possible marketplaces (global), per-part marketplace flags, and update mutation.
 * Pass partId undefined when only `possibleMarketplaces` is needed (e.g. bulk list modal).
 */
export default function usePartMarketplaces(partId: string | undefined) {
  const { get, put } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60; // 1 minute
  const partScoped = Boolean(partId);

  const possibleMarketplaces = useQuery({
    queryKey: ["marketplaces", "possible"],
    queryFn: () =>
      get<DefaultResponseWithData<IPartMarketplaces>>(`/part/marketplaces`),
    staleTime,
    refetchOnWindowFocus: false,
  });

  const marketplaces = useQuery({
    queryKey: ["marketplaces", partId],
    queryFn: () =>
      get<DefaultResponseWithData<IPartMarketplaces>>(
        `/part/${partId}/marketplaces`
      ),
    staleTime,
    refetchOnWindowFocus: false,
    enabled: partScoped,
  });

  const updateMarketplaces = useMutation(
    (body: IPartMarketplaces) => put(`/part/${partId}/marketplaces`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["marketplaces", partId]);
      },
    }
  );

  return {
    possibleMarketplaces,
    marketplaces,
    updateMarketplaces,
  };
}
